import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faTrash, faPenToSquare, faInbox } from '@fortawesome/free-solid-svg-icons';
import { apiJson } from '../../../components/apiClient';

// Caixa de sugestões da automação.
//
// É a contraparte visível do princípio que rege as automações do backend
// (`automacoes/models.py`): nada que fale com o cliente ou mexa em dinheiro
// acontece sozinho. O sistema detecta, prepara e ESPERA aqui.
//
// Por isso o rascunho é editável antes de aprovar: o texto gerado pela IA é
// ponto de partida, e o que a pessoa mandar é o que fica registrado como
// enviado — não o que a IA propôs.

const ROTULO_TIPO = {
  COBRANCA_WHATSAPP: 'Cobrança',
  RESPOSTA_WHATSAPP: 'Resposta',
  REPOSICAO_ESTOQUE: 'Estoque',
};

const ROTULO_INTENCAO = {
  promessa_pagamento: 'prometeu pagar',
  ja_pagou: 'diz que já pagou',
  contesta_valor: 'contesta o valor',
  pede_prazo: 'pede prazo',
  duvida: 'tem dúvida',
  reclamacao: 'reclamação',
  nao_relacionado: 'não relacionado',
  outro: 'outro',
};

function Sugestao({ item, onAprovar, onDescartar, ocupado }) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(item.conteudo?.rascunho || '');
  // Telefone digitado na hora, quando o cadastro do cliente está sem número.
  // Antes a sugestão só avisava "cadastre antes de enviar" e travava o botão
  // — um beco sem saída: a pessoa via a cobrança pronta e não tinha como
  // seguir sem sair da tela, achar o cliente e voltar.
  const [telefoneManual, setTelefoneManual] = useState('');
  const [salvarNoCadastro, setSalvarNoCadastro] = useState(true);

  const conteudo = item.conteudo || {};
  const temMensagem = item.tipo !== 'REPOSICAO_ESTOQUE';
  const telefoneCadastrado = (conteudo.telefone || '').trim();
  const telefoneEfetivo = telefoneCadastrado || telefoneManual.trim();
  const faltaTelefone = temMensagem && !telefoneEfetivo;

  return (
    <div className="crm-sug-item">
      <div className="crm-sug-head">
        <span className="crm-sug-tag">{ROTULO_TIPO[item.tipo] || item.tipo}</span>
        {conteudo.urgente && <span className="crm-sug-tag crm-sug-tag-urgente">urgente</span>}
        {conteudo.intencao && (
          <span className="crm-sug-intencao">{ROTULO_INTENCAO[conteudo.intencao] || conteudo.intencao}</span>
        )}
      </div>

      <div className="crm-sug-titulo">{item.titulo}</div>
      {item.resumo && <div className="crm-sug-resumo">{item.resumo}</div>}

      {conteudo.mensagem_recebida && (
        <div className="crm-sug-recebida">
          <span className="crm-sug-recebida-label">Cliente escreveu</span>
          {conteudo.mensagem_recebida}
        </div>
      )}

      {temMensagem && (
        editando ? (
          <textarea
            className="crm-sug-textarea"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={5}
            aria-label="Mensagem a enviar"
          />
        ) : (
          <div className="crm-sug-rascunho">{texto || <em>Sem rascunho gerado.</em>}</div>
        )
      )}

      {conteudo.promessa_pagamento && (
        <div className="crm-sug-promessa">Prazo mencionado: <strong>{conteudo.promessa_pagamento}</strong></div>
      )}

      {temMensagem && !telefoneCadastrado && (
        <div className="crm-sug-telefone">
          <label className="crm-sug-telefone-label" htmlFor={`tel-${item.id}`}>
            Este contato não tem WhatsApp cadastrado — informe o número para enviar
          </label>
          <input
            id={`tel-${item.id}`}
            className="crm-sug-telefone-input"
            type="tel"
            inputMode="tel"
            placeholder="(11) 98888-7777"
            value={telefoneManual}
            onChange={(e) => setTelefoneManual(e.target.value)}
          />
          {conteudo.cliente_id && (
            <label className="crm-sug-telefone-salvar">
              <input
                type="checkbox"
                checked={salvarNoCadastro}
                onChange={(e) => setSalvarNoCadastro(e.target.checked)}
              />
              Salvar no cadastro do cliente após enviar
            </label>
          )}
        </div>
      )}

      <div className="crm-sug-acoes">
        <button
          className="crm-btn crm-btn-primary crm-btn-sm"
          disabled={ocupado || faltaTelefone || (temMensagem && !texto.trim())}
          onClick={() => onAprovar(item, {
            rascunho: texto,
            // Só manda telefone quando foi digitado agora — não sobrescreve
            // o que já veio do cadastro.
            ...(telefoneCadastrado ? {} : { telefone: telefoneManual.trim(), salvar_telefone: salvarNoCadastro }),
          })}
        >
          <FontAwesomeIcon icon={faCheck} /> {temMensagem ? 'Aprovar e enviar' : 'Aprovar'}
        </button>
        {temMensagem && (
          <button className="crm-btn crm-btn-outline crm-btn-sm" disabled={ocupado} onClick={() => setEditando((v) => !v)}>
            <FontAwesomeIcon icon={faPenToSquare} /> {editando ? 'Concluir edição' : 'Editar'}
          </button>
        )}
        <button className="crm-btn crm-btn-ghost crm-btn-sm" disabled={ocupado} onClick={() => onDescartar(item)}>
          <FontAwesomeIcon icon={faTrash} /> Descartar
        </button>
      </div>
    </div>
  );
}

function SugestoesPanel({ open, onClose, onAlterou, showToast }) {
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [ocupadoId, setOcupadoId] = useState(null);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      // `apiJson` já resolve baseURL e o header Authority a partir do cookie
      // `info` — é o mesmo cliente usado pelo resto do CRM.
      const dados = await apiJson('/automacoes/sugestoes?status=PENDENTE');
      if (!dados || dados.status !== 'ok') throw new Error(dados?.mensagem || 'Não foi possível carregar as sugestões.');
      setItens(dados.sugestoes || []);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { if (open) carregar(); }, [open, carregar]);

  const agir = async (item, caminho, corpo) => {
    setOcupadoId(item.id);
    try {
      const dados = await apiJson(`/automacoes/sugestoes/${item.id}/${caminho}`, {
        method: 'POST',
        body: JSON.stringify(corpo || {}),
      });
      if (!dados || dados.status !== 'ok') throw new Error(dados?.mensagem || 'A ação falhou.');
      // Some da lista de pendentes — a lista mostra só o que ainda espera decisão.
      setItens((atual) => atual.filter((i) => i.id !== item.id));
      if (onAlterou) onAlterou();
      if (showToast) showToast(caminho === 'aprovar' ? 'Sugestão aprovada e executada.' : 'Sugestão descartada.', 'success');
    } catch (e) {
      if (showToast) showToast(e.message, 'error');
      else setErro(e.message);
    } finally {
      setOcupadoId(null);
    }
  };

  if (!open) return null;

  return (
    // Mesmas classes do Assistente (`crm-overlay crm-sheet-overlay` +
    // `crm-sheet crm-agent-sheet`): são elas que dão posicionamento fixo,
    // largura máxima e ALTURA LIMITADA ao painel. Na primeira versão eu
    // inventei `crm-agent-panel`/`crm-agent-overlay`, que não existem em
    // lugar nenhum — sem regra de CSS o painel virou um bloco comum no
    // fluxo da página, cresceu até caber todas as sugestões e quem rolava
    // era a página inteira, levando o cabeçalho embora.
    <div className="crm-overlay crm-sheet-overlay" onClick={onClose}>
      <div className="crm-sheet crm-agent-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Sugestões da automação">
        <div className="crm-modal-header">
          <div>
            <h3 className="crm-modal-title"><FontAwesomeIcon icon={faInbox} /> Sugestões</h3>
            <div className="crm-agent-header-sub">
              {itens.length > 0 ? `${itens.length} esperando sua decisão` : 'Nada esperando decisão'}
            </div>
          </div>
          <button className="crm-icon-btn" onClick={onClose} aria-label="Fechar">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* `crm-agent-body` tem `overflow: hidden` de propósito — no Assistente
            ela só CONTÉM, e quem rola é o `crm-agent-messages` interno. Sem
            esse container de rolagem a lista de sugestões ficava cortada na
            altura do painel, sem scroll nenhum. */}
        <div className="crm-agent-body">
          <div className="crm-sug-lista">
          {carregando && <div className="crm-sug-vazio">Carregando…</div>}
          {erro && <div className="crm-sug-alerta">{erro}</div>}

          {!carregando && !erro && itens.length === 0 && (
            <div className="crm-sug-vazio">
              <p>Nenhuma sugestão pendente.</p>
              <p className="crm-sug-vazio-dica">
                Quando uma conta vencer ou um cliente responder no WhatsApp, a automação prepara
                a ação aqui — e espera você aprovar antes de qualquer envio.
              </p>
            </div>
          )}

          {itens.map((item) => (
            <Sugestao
              key={item.id}
              item={item}
              ocupado={ocupadoId === item.id}
              onAprovar={(i, conteudo) => agir(i, 'aprovar', { conteudo })}
              onDescartar={(i) => agir(i, 'descartar')}
            />
          ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SugestoesPanel;
